import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";
import type { PdpPageProductData } from "~/features/productDetails/models/PdpPageProductData";
import { type ReactNode, useEffect, useState } from "react";
import ProductInformationTab from "~/features/productDetails/components/PdpTabs/ProductInformationTab";
import { CalculationTab } from "~/features/productDetails/components/PdpTabs/CalculationTab";
import CadTab from "~/features/productDetails/components/PdpTabs/CadTab/CadTab";
import MountingManagerTab from "~/features/productDetails/components/PdpTabs/MountingManagerTab";
import { GreaseAppTab } from "~/features/productDetails/components/PdpTabs/GreaseAppTab";
import { isNonNull } from "~/common/utils/reducers";
import { MoreInformationTab } from "~/features/productDetails/components/PdpTabs/MoreInformationTab";
import { scrollToPdpTabs } from "~/features/productDetails/utils/pdpTabNavigation";
import { trackPdpTabActivation, trackPdpTabChange } from "~/app/analytics/ga/ga";
import { ConfiguratorTab } from "~/features/productDetails/components/PdpTabs/ConfiguratorTab";
import { CURRENT_LANGUAGE, MOUNTING_MANAGER_TAB } from "~/utils/constants";
import { TabId } from "~/features/productDetails/models/TabId";
import { ZoovuZoe } from "~/domains/zoe/ZoovuZoe";
import { isSiteRegion } from "~/utils/siteModes";
import { CountryCode } from "~/utils/countryData";
import { KAMELEOON_FLAGS, useKameleoonFlag } from "~/common/config/kameleoonFlags";

export const TAB_ORDER: TabId[] = [
  TabId.PRODUCT_INFORMATION,
  TabId.ZOE,
  TabId.MORE_INFORMATION,
  TabId.CALCULATION,
  TabId.CAD_DATA,
  TabId.MOUNTING_MANAGER,
  TabId.GREASE_APP,
  TabId.CONFIGURATOR,
  TabId.SIZING,
];

export const ZOE_FIRST_TAB_ORDER: TabId[] = [TabId.ZOE, TabId.PRODUCT_INFORMATION, ...TAB_ORDER.slice(2)];

type ZoePdpVariant = "a" | "b" | "c";

export const getTabOrder = (zoePdpVariant: ZoePdpVariant): TabId[] => {
  return zoePdpVariant === "b" ? ZOE_FIRST_TAB_ORDER : TAB_ORDER;
};

type Details = {
  isVisible: (product: ProductDescriptor<PdpPageProductData>) => boolean;
  renderTab: (product: ProductDescriptor<PdpPageProductData>) => ReactNode;
  title: string;
  testId: string;
  action?: (product: ProductDescriptor<PdpPageProductData>) => void;
};

export const usePdpTabs = (product: ProductDescriptor<PdpPageProductData>, activePDPTab: TabId) => {
  const zoePdpVariant = useKameleoonFlag(KAMELEOON_FLAGS.zoePDP);
  const { preferredTab: initialTab, fromUrl } = calculateInitialTab(product, activePDPTab, zoePdpVariant);
  const [activeTabIndex, setActiveTab] = useState<string>(initialTab);

  const onTabActivationHandler = (tabId: string) => {
    setActiveTab(tabId);
    trackPdpTabChange(tabId);
    trackPdpTabActivation(activeTabIndex, false);
    trackPdpTabActivation(tabId, true);
    window.location.hash = tabId;
    scrollToPdpTabs();
    const details = tabDetails[tabId as TabId];
    if (details?.action) {
      details.action(product);
    }
  };

  useEffect(() => {
    if (fromUrl) {
      window.setTimeout(scrollToPdpTabs, 500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tabs = getTabOrder(zoePdpVariant)
    .map((tabId) => {
      const details = tabDetails[tabId];
      if (!isTabVisible(tabId, product, zoePdpVariant)) {
        return null;
      }
      return {
        component: details.renderTab(product),
        title: details.title,
        testId: details.testId,
        value: tabId,
      };
    })
    .filter(isNonNull);

  return {
    tabs,
    activeTabIndex,
    onTabActivationHandler,
  };
};

const tabDetails: Record<TabId, Details> = {
  [TabId.ZOE]: {
    isVisible: (product) => {
      return (
        isSiteRegion(CountryCode.GERMANY) &&
        CURRENT_LANGUAGE === "de" &&
        product.base.productAllCategories.includes("ROTARY")
      );
    },
    renderTab: (product) => <ZoovuZoe display="on-page" region="eu" locale="de-DE" sku={product.base.code} />,
    title: "zoovuZoe.zoe.button.text",
    testId: "zoe-tab",
  },
  [TabId.PRODUCT_INFORMATION]: {
    isVisible: (product) => {
      return (
        !!product.base.specificationBand?.length ||
        product.base.techImages.length > 0 ||
        product.base.technicalDetails.length > 0 ||
        product.base.commentsFootnotes.length > 0
      );
    },
    renderTab: (product) => <ProductInformationTab product={product.base} />,
    title: "productDetailsPage.tabs.information",
    testId: "product-information-tab",
  },
  [TabId.CALCULATION]: {
    isVisible: (product) => {
      if (product.base.externalProduct) {
        return !!product.base.additionalCalculationUrl;
      }
      return !!product.base.bearingXDesignation;
    },
    renderTab: (product) => <CalculationTab product={product.base} />,
    title: "productDetailsPage.tabs.calculation",
    testId: "calculation-tab",
  },
  [TabId.CAD_DATA]: {
    isVisible: (product) => {
      return true;
    },
    renderTab: (product) => <CadTab product={product.base} />,
    title: "productDetailsPage.tabs.caddata",
    testId: "cad-tab",
  },
  [TabId.MOUNTING_MANAGER]: {
    isVisible: (product) => {
      return !!product.base.mountingManagerURL;
    },
    renderTab: (product) => (
      <MountingManagerTab mountingManagerURL={product.base.mountingManagerURL} designation={product.base.designation} />
    ),
    title: MOUNTING_MANAGER_TAB,
    testId: "mounting-manager-tab",
  },
  [TabId.MORE_INFORMATION]: {
    isVisible: (product) => {
      return (product.base.technicalInfo?.columns?.length ?? 0) > 0;
    },
    renderTab: (product) => <MoreInformationTab data={product.base.technicalInfo} />,
    title: "page.pdp.tab.moreInfo",
    testId: "moreInfo-tab",
  },
  [TabId.GREASE_APP]: {
    isVisible: (product) => {
      return !!product.base.greaseAppUrl;
    },
    renderTab: (product) => (
      <GreaseAppTab greaseAppUrl={product.base.greaseAppUrl} designation={product.base.designation} />
    ),
    title: "productDetailsPage.tabs.grease.title",
    testId: "grease-tab",
  },
  [TabId.CONFIGURATOR]: {
    isVisible: (product) => {
      if (product.base.externalProduct) {
        return !!product.base.lmbtConfigurator;
      }
      return false;
    },
    renderTab: (product) => <ConfiguratorTab product={product.base} />,
    title: "productDetailsPage.tabs.configurator.title",
    testId: "configurator-tab",
  },
  [TabId.SIZING]: {
    isVisible: (product) => {
      return !!product.base.sizingUrl;
    },
    renderTab: (product) => null,
    action: (product) => {
      window.open(product.base.sizingUrl);
    },
    title: "productDetailsPage.tabs.sizing.title",
    testId: "sizing-tab",
  },
};

const isTabVisible = (
  tabId: TabId,
  product: ProductDescriptor<PdpPageProductData>,
  zoePdpVariant: ZoePdpVariant,
): boolean => {
  if (tabId === TabId.ZOE && zoePdpVariant === "a") {
    return false;
  }

  return tabDetails[tabId].isVisible(product);
};

export function calculateInitialTab(
  product: ProductDescriptor<PdpPageProductData>,
  activePDPTab: TabId,
  zoePdpVariant: ZoePdpVariant,
): {
  preferredTab: TabId | "";
  fromUrl: boolean;
} {
  const url = new URL(window.location.href);
  const hash = decodeURI(url.hash.substring(1));

  let fromUrl = false;
  let preferredTab: TabId | "";
  if (hash) {
    // New mode
    preferredTab = hash as TabId;
    fromUrl = true;
  } else {
    preferredTab = "";
  }

  if (activePDPTab && tabDetails[activePDPTab] && isTabVisible(activePDPTab, product, zoePdpVariant)) {
    return {
      preferredTab: activePDPTab,
      fromUrl,
    };
  }

  if (preferredTab && tabDetails[preferredTab] && isTabVisible(preferredTab, product, zoePdpVariant)) {
    return {
      preferredTab,
      fromUrl,
    };
  }

  if (zoePdpVariant === "b" && isTabVisible(TabId.ZOE, product, zoePdpVariant)) {
    return {
      preferredTab: TabId.ZOE,
      fromUrl,
    };
  }

  if (isTabVisible(TabId.PRODUCT_INFORMATION, product, zoePdpVariant)) {
    return {
      preferredTab: TabId.PRODUCT_INFORMATION,
      fromUrl,
    };
  }

  return {
    preferredTab: "",
    fromUrl,
  };
}
